/**
 * Shared terrain and object painters for built-in courses.
 *
 * Courses are authored programmatically: a 160×30 grid plus placements.
 * Conventions (tile coords, row 0 at the top):
 *   - A "surface row" is the first solid row of a floor. A body standing on it
 *     occupies `surface - 1`, so enemies, blocks, startY and the goal sit one
 *     row above their floor.
 *   - A crate / ? block floats 2–4 rows above a walkable surface.
 *   - Gaps are usually ≤ 4 tiles of open air at the same height (walk-clearable).
 *   - A castle is 4×4, bottom-left at the same tx as the goal.
 */

import { COURSE_TILES_W, COURSE_TILES_H } from '../engine/constants'
import { Terrain, type ObjectPlacement } from '../game/types'

/** Course width in tiles. */
export const W = COURSE_TILES_W
/** Course height in tiles. */
export const H = COURSE_TILES_H

/** The mutable 4800-entry terrain grid a level is painted into. */
export type Grid = number[]

/** A fresh, entirely empty course grid. */
export function blank(): Grid {
  return new Array<number>(W * H).fill(Terrain.EMPTY)
}

/** Paint one tile. */
export function put(g: Grid, tx: number, ty: number, t: number): void {
  if (tx < 0 || ty < 0 || tx >= W || ty >= H) return
  g[ty * W + tx] = t
}

/** Paint an inclusive rectangle. */
export function box(
  g: Grid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  t: number,
): void {
  for (let ty = y0; ty <= y1; ty++)
    for (let tx = x0; tx <= x1; tx++) put(g, tx, ty, t)
}

/** Paint a horizontal run one tile tall. */
export function strip(
  g: Grid,
  x0: number,
  ty: number,
  x1: number,
  t: number,
): void {
  box(g, x0, ty, x1, ty, t)
}

/** Solid floor from `top` all the way to the bottom of the course. */
export function floor(
  g: Grid,
  x0: number,
  x1: number,
  top: number,
  t: number = Terrain.GROUND,
): void {
  box(g, x0, top, x1, H - 1, t)
}

/** A floating mass of solid blocks: a ledge, a shelf or a plateau. */
export function ledge(
  g: Grid,
  x0: number,
  top: number,
  x1: number,
  bottom: number,
  t: number,
): void {
  box(g, x0, top, x1, bottom, t)
}

/** Carve air back out of a mass, e.g. to dig out a cave. */
export function carve(
  g: Grid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  box(g, x0, y0, x1, y1, Terrain.EMPTY)
}

/** A run of one-way PLATFORM terrain. */
export function catwalk(g: Grid, x0: number, ty: number, x1: number): void {
  strip(g, x0, ty, x1, Terrain.PLATFORM)
}

/**
 * A staircase of solid blocks climbing in the direction of travel. Column `k`
 * is topped at `top - k` and filled down to `bottom`, so the risers are always
 * one tile — comfortably inside a standing jump.
 */
export function stair(
  g: Grid,
  x0: number,
  top: number,
  steps: number,
  dir: 1 | -1,
  bottom: number,
  t: number = Terrain.HARD,
): void {
  for (let k = 0; k < steps; k++)
    box(g, x0 + k * dir, top - k, x0 + k * dir, bottom, t)
}

/** Append one object placement. */
export function place(
  list: ObjectPlacement[],
  id: string,
  tx: number,
  ty: number,
  props?: Record<string, number | string | boolean>,
): void {
  list.push(props === undefined ? { id, tx, ty } : { id, tx, ty, props })
}

/** Append the same object across a run of tiles. */
export function placeRow(
  list: ObjectPlacement[],
  id: string,
  x0: number,
  ty: number,
  x1: number,
  props?: Record<string, number | string | boolean>,
): void {
  for (let tx = x0; tx <= x1; tx++) place(list, id, tx, ty, props)
}

/**
 * A capped vent column: one `pipe` object whose `height` prop is the number of
 * rows it spans. Placed by its top-left tile, so the cap sits on the row you
 * pass as `top` — that row is the lip Tavi stands on — and a vent dropped on a
 * floor whose first solid row is `r` is `pipe(list, tx, r - h + 1, h)`.
 */
export function pipe(
  list: ObjectPlacement[],
  tx: number,
  top: number,
  h: number,
): void {
  if (h < 2) return
  place(list, 'pipe', tx, top, { height: h })
}

/** Evenly spaced coins in a row. */
export function coins(
  list: ObjectPlacement[],
  x0: number,
  x1: number,
  ty: number,
  step = 1,
): void {
  for (let tx = x0; tx <= x1; tx += step) place(list, 'coin', tx, ty)
}

/** The arc of coins that traces a jump: one tile high, two in the middle. */
export function coinArc(
  list: ObjectPlacement[],
  x0: number,
  x1: number,
  ty: number,
): void {
  const span = x1 - x0
  const steps = Math.max(1, Math.round(span / 2))
  for (let i = 0; i <= steps; i++) {
    const tx = x0 + Math.round((span * i) / steps)
    const lift = Math.round(Math.sin((i / steps) * Math.PI) * 2)
    place(list, 'coin', tx, ty - lift)
  }
}

/** A goal flag on `surface`, with the 4x4 castle standing on the same tile. */
export function finish(
  list: ObjectPlacement[],
  gx: number,
  surface: number,
): void {
  place(list, 'goal', gx, surface - 1)
  place(list, 'castle', gx, surface - 4)
}

/** Background dressing for an overworld or sky course. */
export function scenery(
  list: ObjectPlacement[],
  spots: Array<[number, number, string]>,
): void {
  for (const [x, y, id] of spots) place(list, id, x, y)
}

/** Shared LevelData fields for a full-size workshop course. */
export function courseShell(
  name: string,
  theme: import('../game/types').ThemeId,
  time: number,
  startX: number,
  startY: number,
  tiles: Grid,
  objects: ObjectPlacement[],
): import('../game/types').LevelData {
  return {
    name,
    author: 'the workshop',
    width: W,
    height: H,
    theme,
    time,
    startX,
    startY,
    tiles,
    objects,
  }
}
